/**
 * Breadcrumb trail resolver — auto-generates trails from any pathname.
 *
 * Called by AutoBreadcrumbs (client component) and can also be called
 * directly in page components for custom overrides.
 */

export interface BreadcrumbItem {
  name: string;
  href?: string;
}

// Rashi display names
const RASHI_NAMES: Record<string, string> = {
  mesh: "Mesh (Aries)",
  vrishabh: "Vrishabh (Taurus)",
  mithun: "Mithun (Gemini)",
  kark: "Kark (Cancer)",
  singh: "Singh (Leo)",
  kanya: "Kanya (Virgo)",
  tula: "Tula (Libra)",
  vrishchik: "Vrishchik (Scorpio)",
  dhanu: "Dhanu (Sagittarius)",
  makar: "Makar (Capricorn)",
  kumbh: "Kumbh (Aquarius)",
  meen: "Meen (Pisces)",
};

// Hindi rashi names
const RASHI_NAMES_HI: Record<string, string> = {
  mesh: "मेष",
  vrishabh: "वृषभ",
  mithun: "मिथुन",
  kark: "कर्क",
  singh: "सिंह",
  kanya: "कन्या",
  tula: "तुला",
  vrishchik: "वृश्चिक",
  dhanu: "धनु",
  makar: "मकर",
  kumbh: "कुंभ",
  meen: "मीन",
};

function titleCase(slug: string): string {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Pages that should not show auto-breadcrumbs */
const NO_BREADCRUMB_SLUGS = new Set([
  "/",
  "/free-kundali",
  "/ai-astrologer",
  "/ai-kundli",
  "/friends",
  "/friends/invite",
]);

const HI_PAGE_NAMES: Record<string, string> = {
  "free-kundali": "जन्म कुंडली",
  rashifal: "राशिफल",
  "weekly-rashifal": "साप्ताहिक राशिफल",
  "monthly-rashifal": "मासिक राशिफल",
  panchang: "पंचांग",
  choghadiya: "चौघड़िया",
  "rahu-kaal": "राहु काल",
  "kundli-matching": "कुंडली मिलान",
  "gun-milan": "गुण मिलान",
  "nakshatra-calculator": "नक्षत्र कैलकुलेटर",
  "rashi-calculator": "राशि कैलकुलेटर",
  "ascendant-calculator": "लग्न कैलकुलेटर",
  "dasha-calculator": "दशा कैलकुलेटर",
  numerology: "अंक ज्योतिष",
  "name-numerology": "नाम अंक",
  "baby-names": "शिशु नाम",
  "mangal-dosha-calculator": "मंगल दोष जांच",
  compatibility: "राशि मेल",
  muhurat: "शुभ मुहूर्त",
};

const HOROSCOPE_ROUTES: Record<string, string> = {
  "yearly-rashifal": "Yearly Rashifal",
  "career-horoscope": "Career Horoscope",
  "love-horoscope": "Love Horoscope",
  "marriage-horoscope": "Marriage Horoscope",
  "health-horoscope": "Health Horoscope",
};

/**
 * Resolve a breadcrumb trail for a given pathname.
 * Returns null if the page should not show breadcrumbs.
 */
export function resolveTrail(pathname: string): BreadcrumbItem[] | null {
  if (NO_BREADCRUMB_SLUGS.has(pathname)) return null;
  // Strip trailing slash from static-export paths
  const path = pathname.endsWith("/") && pathname !== "/" ? pathname.slice(0, -1) : pathname;
  if (path.startsWith("/hi/")) return resolveHindiTrail(path);
  return resolveEnglishTrail(path);
}

function resolveHindiTrail(path: string): BreadcrumbItem[] | null {
  const home: BreadcrumbItem = { name: "Home", href: "/" };
  const rest = path.slice(4); // strip "/hi/"
  const segments = rest.split("/").filter(Boolean);
  const trail: BreadcrumbItem[] = [home, { name: "हिंदी", href: "/hi/free-kundali" }];

  if (segments.length === 0) return trail;

  const [first, second, third] = segments;

  if (first === "rashifal") return resolveHindiRashifalTrail(trail, second);
  if (first === "rashi") return resolveHindiRashiTrail(trail, second, third);
  if (first === "compatibility") return resolveHindiCompatibilityTrail(trail, second);

  const hiName = HI_PAGE_NAMES[first];
  trail.push({
    name: hiName || titleCase(first.replace(/-/g, " ")),
    ...(!hiName ? {} : { href: undefined }),
  });
  return trail;
}

function resolveHindiRashifalTrail(trail: BreadcrumbItem[], second?: string) {
  trail.push({ name: "राशिफल", href: "/hi/aaj-ka-rashifal" });
  if (second && RASHI_NAMES_HI[second]) trail.push({ name: RASHI_NAMES_HI[second] });
  return trail;
}

function resolveHindiRashiTrail(trail: BreadcrumbItem[], second?: string, third?: string) {
  trail.push({ name: "राशि", href: "/hi/rashi" });
  if (second) {
    trail.push({
      name: RASHI_NAMES_HI[second] || titleCase(second),
      href: `/hi/rashi/${second}`,
    });
    if (third) trail.push({ name: titleCase(third) });
  }
  return trail;
}

function resolveHindiCompatibilityTrail(trail: BreadcrumbItem[], second?: string) {
  trail.push({ name: "राशि मेल", href: "/hi/compatibility" });
  if (second) trail.push({ name: compatibilityPairName(second, RASHI_NAMES_HI) });
  return trail;
}

function resolveEnglishTrail(path: string): BreadcrumbItem[] | null {
  const home: BreadcrumbItem = { name: "Home", href: "/" };
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  const [first, second, third] = segments;
  const directTrail = resolveDirectDynamicTrail(home, first, second, third);
  if (directTrail) return directTrail;

  if (first in HOROSCOPE_ROUTES) return resolveHoroscopeTrail(home, first, second);
  if (first === "mahadasha")
    return resolveSimpleSectionTrail(home, "Mahadasha", "/mahadasha", second);
  if (first === "dosha") return resolveSimpleSectionTrail(home, "Doshas", "/dosha", second);
  if (first === "yoga") return resolveSimpleSectionTrail(home, "Yoga", "/yoga", second);
  if (first === "compatibility") return resolveCompatibilityTrail(home, second);
  if (first === "muhurat")
    return resolveSimpleSectionTrail(home, "Shubh Muhurat", "/muhurat", second);
  if (first === "calendar") return resolveCalendarTrail(home, second, third);

  // dasha-calculator has its own nested layout — single level
  // All other single-segment paths: Home › Page Title
  return [home, { name: titleCase(first.replace(/-/g, " ")) }];
}

function resolveDirectDynamicTrail(
  home: BreadcrumbItem,
  first: string,
  second?: string,
  third?: string
) {
  if (first === "rashifal") return resolveRashifalTrail(home, second);
  if (first === "panchang") return resolveCityTrail(home, "Panchang", "/panchang", second);
  if (first === "choghadiya") return resolveCityTrail(home, "Choghadiya", "/choghadiya", second);
  if (first === "nakshatra") return resolveNakshatraTrail(home, second, third);
  if (first === "rashi") return resolveRashiTrail(home, second, third);
  if (first === "graha") return resolveGrahaTrail(home, second, third);
  return null;
}

function resolveRashifalTrail(home: BreadcrumbItem, second?: string) {
  if (!second) return [home, { name: "Rashifal" }];
  return [
    home,
    { name: "Rashifal", href: "/rashifal" },
    { name: RASHI_NAMES[second] || titleCase(second) },
  ];
}

function resolveCityTrail(home: BreadcrumbItem, name: string, href: string, second?: string) {
  if (!second) return [home, { name }];
  return [home, { name, href }, { name: titleCase(second.replace(/-/g, " ")) }];
}

function resolveNakshatraTrail(home: BreadcrumbItem, second?: string, third?: string) {
  if (!second) return [home, { name: "Nakshatra" }];
  const trail: BreadcrumbItem[] = [home, { name: "Nakshatra", href: "/nakshatra" }];
  const nakshatraName = titleCase(
    second.replace(/-female$|-male$|-compatibility$/, "").replace(/-/g, " ")
  );
  trail.push({ name: nakshatraName, href: `/nakshatra/${second}` });
  if (third) {
    trail.push({ name: third.startsWith("pada-") ? `Pada ${third.slice(5)}` : titleCase(third) });
  }
  return trail;
}

function resolveRashiTrail(home: BreadcrumbItem, second?: string, third?: string) {
  if (!second) return [home, { name: "Rashi" }];
  const trail: BreadcrumbItem[] = [home, { name: "Rashi", href: "/rashi/mesh/" }];
  trail.push({ name: RASHI_NAMES[second] || titleCase(second), href: `/rashi/${second}` });
  if (third) trail.push({ name: titleCase(third) });
  return trail;
}

function resolveGrahaTrail(home: BreadcrumbItem, second?: string, third?: string) {
  if (!second) return [home, { name: "Graha" }];
  const trail: BreadcrumbItem[] = [home, { name: "Graha", href: "/graha" }];
  trail.push({ name: titleCase(second), href: `/graha/${second}` });
  if (third) {
    trail.push({ name: grahaChildName(third) });
  }
  return trail;
}

function grahaChildName(third: string) {
  const houseMatch = third.match(/^in-(\d+)-house$/);
  if (houseMatch) {
    const n = parseInt(houseMatch[1], 10);
    return `${n}${ordinalSuffix(n)} House`;
  }
  if (!third.startsWith("in-")) return titleCase(third);
  const rashiId = third.slice(3);
  return RASHI_NAMES[rashiId] || titleCase(rashiId);
}

function ordinalSuffix(n: number) {
  if (n === 1) return "st";
  if (n === 2) return "nd";
  if (n === 3) return "rd";
  return "th";
}

function resolveHoroscopeTrail(home: BreadcrumbItem, first: string, second?: string) {
  const trail: BreadcrumbItem[] = [
    home,
    { name: "Rashifal", href: "/rashifal" },
    { name: HOROSCOPE_ROUTES[first], href: `/${first}` },
  ];
  if (second) {
    trail.push({ name: horoscopeChildName(second) });
  }
  return trail;
}

function horoscopeChildName(second: string) {
  const match = second.match(/^([a-z]+)-(\d{4})$/);
  if (!match) return titleCase(second);
  const rashi = RASHI_NAMES[match[1]] || titleCase(match[1]);
  return `${rashi} ${match[2]}`;
}

function resolveSimpleSectionTrail(
  home: BreadcrumbItem,
  name: string,
  href: string,
  second?: string
) {
  const trail: BreadcrumbItem[] = [home, { name, href }];
  if (second) {
    trail.push({
      name: name === "Mahadasha" ? `${titleCase(second)} Mahadasha` : titleCase(second),
    });
  }
  return trail;
}

function resolveCompatibilityTrail(home: BreadcrumbItem, second?: string) {
  const trail: BreadcrumbItem[] = [home, { name: "Compatibility", href: "/compatibility" }];
  if (second) trail.push({ name: compatibilityPairName(second, RASHI_NAMES) });
  return trail;
}

function compatibilityPairName(slug: string, names: Record<string, string>) {
  return slug
    .split("-and-")
    .map((segment) => names[segment] || titleCase(segment))
    .join(" & ");
}

function resolveCalendarTrail(home: BreadcrumbItem, second?: string, third?: string) {
  const trail: BreadcrumbItem[] = [home, { name: "Hindu Calendar", href: "/calendar" }];
  if (second === "festival") {
    trail.push({ name: "Festivals", href: "/calendar/festival" });
    if (third) trail.push({ name: titleCase(third) });
  }
  return trail;
}
